import React, { useEffect, useCallback } from "react";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

export interface LightboxItem {
  img: string;
  title?: string;
  alt?: string;
}

interface ImageLightboxModalProps {
  open: boolean;
  currentIndex: number;
  items: LightboxItem[];
  onClose: () => void;
  onNavigate: (newIndex: number) => void;
}

export const ImageLightboxModal: React.FC<ImageLightboxModalProps> = ({
  open,
  currentIndex,
  items,
  onClose,
  onNavigate,
}) => {
  const currentItem = items[currentIndex];

  const handlePrev = useCallback(() => {
    if (items.length === 0) return;
    const prevIndex = (currentIndex - 1 + items.length) % items.length;
    onNavigate(prevIndex);
  }, [currentIndex, items.length, onNavigate]);

  const handleNext = useCallback(() => {
    if (items.length === 0) return;
    const nextIndex = (currentIndex + 1) % items.length;
    onNavigate(nextIndex);
  }, [currentIndex, items.length, onNavigate]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handlePrev, handleNext, onClose]);

  if (!open || !currentItem) return null;

  const captionText = currentItem.title || currentItem.alt || "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "hidden",
          margin: { xs: 1, sm: 2 },
          maxHeight: "95vh",
          maxWidth: "95vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(10, 8, 7, 0.92)",
            backdropFilter: "blur(8px)",
          },
        },
      }}
    >
      {/* Botón de cerrar */}
      <IconButton
        onClick={onClose}
        aria-label="Cerrar vista ampliada"
        sx={{
          position: "fixed",
          top: { xs: 12, md: 24 },
          right: { xs: 12, md: 24 },
          color: "#FFFFFF",
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(4px)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            transform: "scale(1.08)",
          },
          transition: "all 0.2s ease-in-out",
          zIndex: 1400,
          width: 44,
          height: 44,
        }}
      >
        <FontAwesomeIcon icon={faTimes} style={{ fontSize: "1.2rem" }} />
      </IconButton>

      {/* Botón Anterior */}
      {items.length > 1 && (
        <IconButton
          onClick={handlePrev}
          aria-label="Imagen anterior"
          sx={{
            position: "fixed",
            left: { xs: 8, md: 24 },
            top: "50%",
            transform: "translateY(-50%)",
            color: "#FFFFFF",
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(4px)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              transform: "translateY(-50%) scale(1.08)",
            },
            transition: "all 0.2s ease-in-out",
            zIndex: 1400,
            width: 48,
            height: 48,
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "1.2rem" }} />
        </IconButton>
      )}

      {/* Contenedor Principal de la Imagen */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "100%",
          maxHeight: "82vh",
        }}
      >
        <Box
          component="img"
          src={currentItem.img}
          alt={captionText}
          sx={{
            maxHeight: "78vh",
            maxWidth: "90vw",
            objectFit: "contain",
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.6)",
            border: "1px solid rgba(212, 175, 55, 0.3)",
            userSelect: "none",
          }}
        />

        {/* Leyenda y Contador */}
        <Box
          sx={{
            mt: 2,
            textAlign: "center",
            px: 2,
            py: 1,
            borderRadius: "24px",
            backgroundColor: "rgba(28, 22, 18, 0.75)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(212, 175, 55, 0.2)",
            maxWidth: "85vw",
          }}
        >
          {captionText && (
            <Typography
              variant="body2"
              sx={{
                color: "#F3EBDD",
                fontWeight: 500,
                fontSize: { xs: "0.85rem", md: "0.95rem" },
                mb: 0.25,
              }}
            >
              {captionText}
            </Typography>
          )}
          {items.length > 1 && (
            <Typography
              variant="caption"
              sx={{
                color: "#D4AF37",
                fontSize: "0.75rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
              }}
            >
              {currentIndex + 1} / {items.length}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Botón Siguiente */}
      {items.length > 1 && (
        <IconButton
          onClick={handleNext}
          aria-label="Imagen siguiente"
          sx={{
            position: "fixed",
            right: { xs: 8, md: 24 },
            top: "50%",
            transform: "translateY(-50%)",
            color: "#FFFFFF",
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(4px)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              transform: "translateY(-50%) scale(1.08)",
            },
            transition: "all 0.2s ease-in-out",
            zIndex: 1400,
            width: 48,
            height: 48,
          }}
        >
          <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: "1.2rem" }} />
        </IconButton>
      )}
    </Dialog>
  );
};
